import {
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControlLabel,
  IconButton,
  ListItem,
  ListItemIcon,
  MenuItem,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import ChatIcon from "@material-ui/icons/Chat";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import InfoIcon from "@material-ui/icons/Info";
import NotificationsIcon from "@material-ui/icons/Notifications";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import SortableTable from "../../Components/SortableTable";
import Title from "../../Components/Title";

import { useTranslation } from "react-i18next";
import {
  ECABIN,
  findValueInText,
  formatClientInfoString,
  formatTimeWithTZOffset,
  getInvoiceIssuerData,
  getInvoiceIssuerOptions,
  getInvoiceIssuers,
  getToken,
  getUser,
  invoiceIssuerMatchesValue,
  paymentStates,
  requestErrorHandler,
  searchMatchingList,
  shouldShowOrderSerialNoPreallocationLabel,
} from "../../Utils/Common";
import EnvSettings from "../../Utils/EnvSettings";
import { useStyles } from "../../Utils/Styles";
import EditOrder from "./EditOrder";
import NewOrder from "./NewOrder";

const server = EnvSettings.server;
const DEFAULT_ORDER_FILTER_STATES = [
  "waiting",
  "invoiced",
  "deposit",
  "completed",
  "transit",
];

const getInitialFilterStates = (orderId) =>
  orderId ? paymentStates.map((m) => m.value) : DEFAULT_ORDER_FILTER_STATES;

const mergeOrdersById = (currentOrders, newOrders) => {
  const ordersById = new Map();
  [...(currentOrders || []), ...(newOrders || [])].forEach((order) => {
    if (order?.id) {
      ordersById.set(order.id, order);
    }
  });
  return Array.from(ordersById.values());
};

const getFilterDateTime = (date, endOfDay) => {
  if (!date) {
    return undefined;
  }
  const parsedDate = new Date(
    date + (endOfDay ? "T23:59:59.999" : "T00:00:00"),
  );
  const time = parsedDate.getTime();
  return Number.isNaN(time) ? undefined : time;
};

const isDateInRange = (dateValue, dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) {
    return true;
  }
  if (!dateValue) {
    return false;
  }
  const dateTime = new Date(dateValue).getTime();
  if (Number.isNaN(dateTime)) {
    return false;
  }
  const fromTime = getFilterDateTime(dateFrom);
  const toTime = getFilterDateTime(dateTo, true);
  return (
    (fromTime === undefined || dateTime >= fromTime) &&
    (toTime === undefined || dateTime <= toTime)
  );
};

const getOrderDisplayUpdatedAt = (order) => {
  if (order?.Notes && order.Notes.length) {
    const latestNote = order.Notes.reduce((prev, current) =>
      prev && prev.id > current.id ? prev : current,
    );
    return latestNote?.updatedAt;
  }
  return order?.activityUpdatedAt || order?.updatedAt;
};

const getOrderStatistics = (orders) => {
  const itemsByKey = new Map();
  (orders || []).forEach((order) => {
    (Array.isArray(order?.genericDevices) ? order.genericDevices : []).forEach(
      (item) => {
        const name = item?.name || "";
        const sku = item?.sku || "";
        const key = sku || name;
        if (!key) {
          return;
        }
        const parsedCount = Number(item?.count);
        const count = Number.isFinite(parsedCount) ? parsedCount : 1;
        const parsedPrice = Number(item?.price ?? item?.listPrice ?? 0);
        const price = Number.isFinite(parsedPrice) ? parsedPrice : 0;
        const parsedDiscount = Number(item?.discount || 0);
        const discount = Number.isFinite(parsedDiscount) ? parsedDiscount : 0;
        const aggregatePrice = count * price * (1 - discount / 100);
        const current = itemsByKey.get(key) || {
          name,
          sku,
          count: 0,
          aggregatePrice: 0,
          averagePrice: 0,
        };
        current.count += count;
        current.aggregatePrice += aggregatePrice;
        current.averagePrice = current.count
          ? current.aggregatePrice / current.count
          : 0;
        itemsByKey.set(key, current);
      },
    );
  });

  return {
    orderCount: orders?.length || 0,
    itemStats: Array.from(itemsByKey.values()).sort(
      (a, b) => b.count - a.count,
    ),
  };
};

export default function Orders(props) {
  const order_id = props?.computedMatch?.params?.order_id;
  const classes = useStyles();
  const [searchList, setSearchList] = useState();
  const [orderList, setOrderList] = useState();
  const [searchValue, setSearchValue] = useState(() => order_id || "");
  const [orderFilters, setOrderFilters] = useState({});
  const [expandOrderItems, setExpandOrderItems] = useState(false);
  const [showAdditionalFilters, setShowAdditionalFilters] = useState(false);
  const [showOrderStatistics, setShowOrderStatistics] = useState(false);
  const [invoiceIssuers, setInvoiceIssuers] = useState([]);
  const [filterStates, setFilterStates] = useState(() =>
    getInitialFilterStates(order_id),
  );
  const [fetchedFilterStates, setFetchedFilterStates] = useState([]);
  const [loadingFilterStates, setLoadingFilterStates] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const editBoxRef = useRef(null);

  const updateOrderFilter = (key, value) => {
    setOrderFilters((currentFilters) => ({
      ...currentFilters,
      [key]: value || undefined,
    }));
  };

  const orderStatistics = useMemo(
    () => getOrderStatistics(searchList),
    [searchList],
  );

  useEffect(() => {
    getInvoiceIssuers(setInvoiceIssuers, dispatch);
  }, [dispatch]);

  const checkForComments = (order) => {
    if (order?.hasComments || order?.Notes?.find((f) => f.User)) {
      return (
        <Grid item xs={6}>
          <Tooltip title={t("activeComments")}>
            <ChatIcon color="primary" style={{ fontSize: 20 }} />
          </Tooltip>
        </Grid>
      );
    }
    return null;
  };

  const checkForEmailSubscription = (order) => {
    if (
      order?.isSubscribed ||
      order.EmailListOrders?.find(
        (f) =>
          f &&
          f.EmailList?.EmailListUsers.find(
            (u) => u.User.id === getUser().userId,
          ),
      ) ||
      order.UserId === getUser().userId
    ) {
      return (
        <Grid item xs={6}>
          <Tooltip title={t("tagged")}>
            <NotificationsIcon color="primary" style={{ fontSize: 20 }} />
          </Tooltip>
        </Grid>
      );
    }
    return null;
  };

  const checkHubspotIntegration = (order) => {
    if (order?.metadata?.dealId) {
      return (
        <Grid item xs={6}>
          <Tooltip
            title={
              t("orders.createdFromHubspot") + " " + order?.metadata?.dealId
            }
          >
            <svg
              viewBox="0 0 16 16"
              height="20px"
              data-test-id="icon-hubspot-logo"
              role="img"
              aria-label="HubSpot logo"
            >
              <title>HubSpot</title>
              <path
                d="M11.6127 10.9174C10.4763 10.9174 9.55512 10.0374 9.55512 8.95201C9.55512 7.86645 10.4763 6.98646 11.6127 6.98646C12.749 6.98646 13.6702 7.86645 13.6702 8.95201C13.6702 10.0374 12.749 10.9174 11.6127 10.9174ZM12.2286 5.16801V3.41954C12.7064 3.20397 13.041 2.74229 13.041 2.20648V2.16612C13.041 1.42664 12.4077 0.821619 11.6336 0.821619H11.5915C10.8174 0.821619 10.1841 1.42664 10.1841 2.16612V2.20648C10.1841 2.74229 10.5187 3.20416 10.9965 3.41972V5.16801C10.2852 5.27306 9.63527 5.55332 9.09927 5.96578L4.07384 2.23138C4.107 2.10973 4.1303 1.9845 4.1305 1.85286C4.13129 1.0155 3.42174 0.335606 2.54479 0.334474C1.66822 0.333532 0.956311 1.01154 0.955323 1.84909C0.954337 2.68665 1.66388 3.36654 2.54084 3.36748C2.82651 3.36786 3.09106 3.29035 3.32283 3.16436L8.26614 6.83803C7.84582 7.44418 7.59944 8.17028 7.59944 8.95201C7.59944 9.77033 7.8701 10.5274 8.32734 11.1499L6.82415 12.5861C6.7053 12.5519 6.58211 12.5282 6.45141 12.5282C5.73101 12.5282 5.14684 13.086 5.14684 13.7742C5.14684 14.4626 5.73101 15.0205 6.45141 15.0205C7.17201 15.0205 7.75599 14.4626 7.75599 13.7742C7.75599 13.6498 7.73112 13.5319 7.69538 13.4184L9.18238 11.9978C9.85738 12.4899 10.698 12.7856 11.6127 12.7856C13.8292 12.7856 15.6257 11.0692 15.6257 8.95201C15.6257 7.03531 14.1517 5.45185 12.2286 5.16801Z"
                fill="#3d57a5"
              ></path>
            </svg>
          </Tooltip>
        </Grid>
      );
    }
    return null;
  };

  const iconBox = (data, d, dd) => {
    var comments = checkForComments(dd);
    var emails = checkForEmailSubscription(dd);
    var hubspot = checkHubspotIntegration(dd);
    if (!comments && !emails && !hubspot) {
      return "";
    }
    return (
      <Grid container spacing={1}>
        {comments}
        {emails}
        {hubspot}
      </Grid>
    );
  };

  const executeScroll = () => {
    editBoxRef.current.scrollIntoView({ behavior: "smooth" });
  };

  const showOrder = (dev) => {
    setSelectedOrder();
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        view: "detail",
        order_id: dev.id,
      }),
    };
    fetch(server + "/get_orders", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          // Hack for refreshing the selected device component
          setTimeout(() => {
            setSelectedOrder(result.orders?.[0] || dev);
            if (editBoxRef && editBoxRef.current) {
              executeScroll();
            }
          }, 1);
        }
      })
      .catch(() => {
        setTimeout(() => {
          setSelectedOrder(dev);
          if (editBoxRef && editBoxRef.current) {
            executeScroll();
          }
        }, 1);
      });
  };

  const getOrders = (statesToFetch, replace) => {
    const paymentStatesToFetch = Array.isArray(statesToFetch)
      ? statesToFetch.filter(Boolean)
      : filterStates;
    if (!paymentStatesToFetch.length) {
      if (replace) {
        setOrderList([]);
        setSearchList([]);
        setFetchedFilterStates([]);
      }
      return;
    }
    setLoading(true);
    setLoadingFilterStates((currentStates) => [
      ...new Set([...currentStates, ...paymentStatesToFetch]),
    ]);
    if (replace) {
      setSearchList();
      setSelectedOrder();
    }
    const requestOptions = {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        token: getToken(),
        view: "list",
        paymentStates: paymentStatesToFetch,
      }),
    };
    fetch(server + "/get_orders", requestOptions)
      .then((res) => res.json())
      .then((result) => {
        requestErrorHandler(result, dispatch);
        if (!result.error) {
          setOrderList((currentOrders) =>
            replace
              ? result.orders
              : mergeOrdersById(currentOrders, result.orders),
          );
          setFetchedFilterStates((currentStates) =>
            replace
              ? paymentStatesToFetch
              : [...new Set([...currentStates, ...paymentStatesToFetch])],
          );
        }
        setLoading(false);
        setLoadingFilterStates((currentStates) =>
          currentStates.filter(
            (state) => !paymentStatesToFetch.includes(state),
          ),
        );
      })
      .catch((e) => {
        setLoading(false);
        setLoadingFilterStates((currentStates) =>
          currentStates.filter(
            (state) => !paymentStatesToFetch.includes(state),
          ),
        );
      });
  };

  useEffect(() => {
    getOrders(getInitialFilterStates(order_id), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterStateChange = (state, selected) => {
    const nextStates = selected
      ? [...new Set([...filterStates, state])]
      : filterStates.filter((filterState) => filterState !== state);
    setFilterStates(nextStates);
    if (selected && !fetchedFilterStates.includes(state)) {
      getOrders([state], false);
    }
  };

  const filterResults = () => {
    setSearchList(
      orderList &&
        orderList.filter((contact) => {
          return (
            filterStates.find((o) => o === contact.paymentStatus) &&
            searchMatchingList(
              [
                contact.number,
                contact.clientInfo,
                contact.genericDevices,
                contact.Devices,
                contact.User,
                contact.metadata,
              ],
              searchValue,
            ) &&
            invoiceIssuerMatchesValue(
              contact.invoiceIssuer,
              orderFilters.invoiceIssuer,
            ) &&
            findValueInText(contact.User?.fullName, orderFilters.createdBy) &&
            (findValueInText(contact.genericDevices, orderFilters.items) ||
              findValueInText(contact.Devices, orderFilters.items)) &&
            isDateInRange(
              contact.createdAt,
              orderFilters.createdFrom,
              orderFilters.createdTo,
            ) &&
            isDateInRange(
              getOrderDisplayUpdatedAt(contact),
              orderFilters.updatedFrom,
              orderFilters.updatedTo,
            )
          );
        }),
    );
  };
  useEffect(filterResults, [
    searchValue,
    orderList,
    filterStates,
    orderFilters.invoiceIssuer,
    orderFilters.createdBy,
    orderFilters.items,
    orderFilters.createdFrom,
    orderFilters.createdTo,
    orderFilters.updatedFrom,
    orderFilters.updatedTo,
    invoiceIssuers,
  ]);

  return (
    <Grid container spacing={3}>
      <NewOrder />
      <Grid item xs={12}>
        <Paper className={classes.paper} style={{ overflow: "hidden" }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item container spacing={3} xs={12}>
              <Grid item xs={12}>
                <Title>{t("orders.header")}</Title>
              </Grid>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                autoFocus
                label={t("search")}
                type="text"
                fullWidth
                variant="outlined"
                value={searchValue}
                onChange={(e) => {
                  setSearchValue(e.target.value.toLowerCase());
                }}
                ref={editBoxRef}
              />
            </Grid>
            <Grid item container spacing={1} xs={12} alignItems="center">
              <Grid item>
                <Title>{t("quickFilters")}</Title>
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowAdditionalFilters(!showAdditionalFilters);
                  }}
                >
                  {showAdditionalFilters ? t("hide") : t("show")}
                </Button>
              </Grid>
            </Grid>
            {showAdditionalFilters ? (
              <>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label={t("orders.invoiceIssuer")}
                    variant="outlined"
                    select
                    fullWidth
                    value={orderFilters.invoiceIssuer || ""}
                    onChange={(e) => {
                      updateOrderFilter("invoiceIssuer", e.target.value);
                    }}
                  >
                    {[
                      { name: "", data: { name: t("select") } },
                      ...getInvoiceIssuerOptions(invoiceIssuers),
                    ].map((m, i) => (
                      <MenuItem key={(m.name || "select") + i} value={m.name}>
                        {m?.data?.name || t("select")}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label={t("orders.createdFrom")}
                    type="date"
                    fullWidth
                    variant="outlined"
                    value={orderFilters.createdFrom || ""}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                      updateOrderFilter("createdFrom", e.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label={t("orders.createdTo")}
                    type="date"
                    fullWidth
                    variant="outlined"
                    value={orderFilters.createdTo || ""}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                      updateOrderFilter("createdTo", e.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label={t("orders.updatedFrom")}
                    type="date"
                    fullWidth
                    variant="outlined"
                    value={orderFilters.updatedFrom || ""}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                      updateOrderFilter("updatedFrom", e.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label={t("orders.updatedTo")}
                    type="date"
                    fullWidth
                    variant="outlined"
                    value={orderFilters.updatedTo || ""}
                    InputLabelProps={{ shrink: true }}
                    onChange={(e) => {
                      updateOrderFilter("updatedTo", e.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label={t("orders.createdBy")}
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={orderFilters.createdBy || ""}
                    onChange={(e) => {
                      updateOrderFilter("createdBy", e.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label={t("orders.itemsFilter")}
                    type="text"
                    fullWidth
                    variant="outlined"
                    value={orderFilters.items || ""}
                    onChange={(e) => {
                      updateOrderFilter("items", e.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="primary"
                        checked={expandOrderItems}
                        onChange={(e, checked) => {
                          setExpandOrderItems(checked);
                        }}
                      />
                    }
                    label={t("orders.expandItems")}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Grid container alignItems="center" spacing={1}>
                    <Grid item>
                      <Typography style={{ fontWeight: "bold" }}>
                        {t("orders.statistics")}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <Typography>
                        {t("orders.filteredOrders")}:{" "}
                        {orderStatistics.orderCount}
                      </Typography>
                    </Grid>
                    <Grid item>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setShowOrderStatistics(!showOrderStatistics);
                        }}
                      >
                        {showOrderStatistics ? (
                          <ExpandLessIcon />
                        ) : (
                          <ExpandMoreIcon />
                        )}
                      </IconButton>
                    </Grid>
                  </Grid>
                  <Collapse in={showOrderStatistics}>
                    <Grid container spacing={2} style={{ marginTop: 4 }}>
                      <Grid item xs={12} md={6}>
                        {orderStatistics.itemStats.length ? (
                          <SortableTable
                            key={
                              orderStatistics.orderCount +
                              "-" +
                              orderStatistics.itemStats.length
                            }
                            headCells={[
                              {
                                id: "name",
                                numeric: false,
                                disablePadding: false,
                                label: t("item"),
                              },
                              {
                                id: "sku",
                                numeric: false,
                                disablePadding: false,
                                label: t("orders.sku"),
                              },
                              {
                                id: "count",
                                numeric: true,
                                disablePadding: false,
                                label: t("orders.allocatedCount"),
                              },
                              {
                                id: "aggregatePrice",
                                numeric: true,
                                disablePadding: false,
                                label: t("orders.aggregatePrice"),
                              },
                              {
                                id: "averagePrice",
                                numeric: true,
                                disablePadding: false,
                                label: t("orders.averagePrice"),
                              },
                            ]}
                            tableData={orderStatistics.itemStats}
                            header={
                              t("orders.order") + " " + t("orders.statistics")
                            }
                            downloadButton={true}
                            defaultSortRow={"count"}
                            defaultOrder={"desc"}
                            disablePagination={true}
                            rowsPerPage={orderStatistics.itemStats.length || 1}
                            tableSize={"small"}
                            handleRowClick={() => {}}
                            specialDataFormatting={[
                              {
                                id: "aggregatePrice",
                                formatReturnsElement: true,
                                format: (data) => Number(data || 0).toFixed(2),
                              },
                              {
                                id: "averagePrice",
                                formatReturnsElement: true,
                                format: (data) => Number(data || 0).toFixed(2),
                              },
                            ]}
                          />
                        ) : (
                          <Typography>
                            {t("orders.noItemsInFilteredOrders")}
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Collapse>
                </Grid>
              </>
            ) : null}
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <EditOrder selectedOrder={selectedOrder} />
          {searchList ? (
            <>
              <Grid container item xs={12}>
                <Grid item xs={11}>
                  <Typography>{t("orders.paymentStatus")}</Typography>
                  {paymentStates.map((o) => {
                    const isStateLoading = loadingFilterStates.includes(
                      o.value,
                    );
                    return (
                      <FormControlLabel
                        key={o.label}
                        control={
                          <Checkbox
                            color="primary"
                            disabled={isStateLoading}
                            style={{
                              color: o.color || "",
                            }}
                            onChange={(e, v) => {
                              handleFilterStateChange(o.value, v);
                            }}
                            checked={!!filterStates.find((f) => f === o.value)}
                          />
                        }
                        label={
                          <>
                            {t("paymentStates." + o.value)}
                            {isStateLoading ? (
                              <CircularProgress
                                size={12}
                                style={{ marginLeft: 6 }}
                              />
                            ) : null}
                          </>
                        }
                      />
                    );
                  })}
                </Grid>
                <Grid item xs={1}>
                  <Typography style={{ textAlign: "center" }}>
                    Backlog Total{" "}
                    {searchList.reduce(
                      (partialSum, a) =>
                        a.paymentStatus === "invoiced" ||
                        a.paymentStatus === "deposit"
                          ? partialSum + a.totalPrice - a.depositAmount
                          : partialSum,
                      0,
                    )}
                  </Typography>
                </Grid>
              </Grid>
              <SortableTable
                header={t("orders.header")}
                tableLayout={"fixed"}
                downloadButton={true}
                headCells={[
                  {
                    id: "Notes",
                    label: "",
                    width: 70,
                    hideFromCsv: true,
                  },
                  {
                    id: "updatedAt",
                    numeric: false,
                    disablePadding: false,
                    label: t("updatedAt"),
                    // width: 105,
                  },
                  {
                    id: "createdAt",
                    numeric: false,
                    disablePadding: false,
                    label: t("createdAt"),
                    // width: 105,
                  },
                  {
                    id: "User.fullName",
                    numeric: false,
                    disablePadding: false,
                    label: t("createdBy"),
                  },
                  {
                    id: "invoiceIssuer",
                    numeric: false,
                    disablePadding: false,
                    label: t("orders.invoiceIssuer"),
                  },
                  {
                    id: "number",
                    numeric: false,
                    disablePadding: false,
                    label: t("orders.orderNumber"),
                  },
                  {
                    id: "paymentStatus",
                    numeric: false,
                    disablePadding: false,
                    label: t("orders.paymentStatus"),
                  },
                  {
                    id: "clientInfo",
                    numeric: false,
                    disablePadding: false,
                    label: t("clientInfo"),
                  },
                  {
                    id: "genericDevices",
                    numeric: false,
                    disablePadding: false,
                    label: t("items"),
                  },
                  {
                    id: "Devices",
                    numeric: false,
                    disablePadding: false,
                    label: t("orders.allocatedSerialNumbers"),
                  },
                  {
                    id: "depositAmount",
                    numeric: false,
                    disablePadding: false,
                    label: "Backlog",
                  },
                ]}
                // disablePagination: true,
                defaultSortRow={"updatedAt"}
                handleRowClick={showOrder}
                getRowStyle={(data) => {
                  var obj = { cursor: "pointer" };
                  var p = paymentStates.find(
                    (f) => data.paymentStatus === f.value,
                  );
                  if (p && p.color) {
                    return { ...obj, backgroundColor: p.color };
                  }
                  return obj;
                }}
                specialDataFormatting={[
                  {
                    id: "updatedAt",
                    format: (data, d, dd) => {
                      if (dd.Notes && dd.Notes.length) {
                        const max = dd.Notes.reduce((prev, current) =>
                          prev && prev.id > current.id ? prev : current,
                        );
                        return formatTimeWithTZOffset(max.updatedAt, ECABIN);
                      }
                      if (dd.activityUpdatedAt) {
                        return formatTimeWithTZOffset(
                          dd.activityUpdatedAt,
                          ECABIN,
                        );
                      }
                      return formatTimeWithTZOffset(data, ECABIN);
                    },
                  },
                  {
                    id: "Notes",
                    format: iconBox,
                  },
                  {
                    id: "createdAt",
                    format: (data) => {
                      return formatTimeWithTZOffset(data, ECABIN);
                    },
                  },
                  {
                    id: "clientInfo",
                    format: (data) => {
                      return formatClientInfoString(data, [
                        "companyName",
                        "contactName",
                        "location",
                      ]);
                    },
                  },
                  {
                    id: "genericDevices",
                    format: (data, dataFormat) => {
                      const items = Array.isArray(data) ? data : [];
                      const formatItemsForCsv = (orderItems) =>
                        orderItems
                          .map((v) =>
                            [
                              v.count,
                              "x",
                              v.name,
                              v.sku ? "(" + v.sku + ")" : "",
                            ]
                              .filter(Boolean)
                              .join(" "),
                          )
                          .join(" | ");
                      if (dataFormat === "json") {
                        return formatItemsForCsv(items);
                      }
                      const renderItems = (orderItems, highlight) => (
                        <>
                          {orderItems.map((v, i) => (
                            <Typography
                              key={(v.sku || v.name || "item") + i}
                              style={{
                                ...(highlight
                                  ? { backgroundColor: "yellow" }
                                  : {}),
                                fontSize: 15,
                              }}
                            >
                              {v.count + " x " + v.name}
                            </Typography>
                          ))}
                        </>
                      );
                      var x;
                      const itemFilterValue = orderFilters.items || searchValue;
                      if (itemFilterValue && itemFilterValue !== "") {
                        x = items.filter(
                          (f) =>
                            (f.name || "")
                              .toLowerCase()
                              .includes(itemFilterValue.toLowerCase()) ||
                            (f.sku || "")
                              .toLowerCase()
                              .includes(itemFilterValue.toLowerCase()),
                        );
                        if (x.length) {
                          return renderItems(x, true);
                        }
                      }
                      if (items.length > 1) {
                        if (expandOrderItems) {
                          return renderItems(items);
                        }
                        x = 0;
                        items.forEach((d) => (x = x + d.count));
                        return t("count") + ":" + x;
                      }
                      var a = "";
                      items.forEach((v) => (a = v.count + " x " + v.name));
                      return a;
                    },
                  },
                  {
                    id: "Devices",
                    format: (data, d, dd) => {
                      const allocationNeeded =
                        shouldShowOrderSerialNoPreallocationLabel(dd);
                      if (data && data.length) {
                        const serials = data
                          .map((m) => m.serialNumber)
                          .join(" ");
                        return allocationNeeded
                          ? serials + " " + t("orders.allocationNeeded")
                          : serials;
                      }
                      if (allocationNeeded) {
                        return t("orders.allocationNeeded");
                      }
                      return "";
                    },
                  },
                  {
                    id: "paymentStatus",
                    formatReturnsElement: true,
                    format: (data, e, v) => {
                      if (e === "json") {
                        return t(
                          "paymentStates." +
                            paymentStates.find((o) => o.value === data).value,
                        );
                      }
                      return (
                        <ListItem>
                          {t(
                            "paymentStates." +
                              paymentStates.find((o) => o.value === data).value,
                          )}
                          {data === "transit" && v.multipleShipments ? (
                            <Tooltip
                              title={t("orders.multipleShipments")}
                              placement="top"
                            >
                              <ListItemIcon size="small">
                                <InfoIcon
                                  style={{ verticalAlign: "middle" }}
                                  fontSize="small"
                                />
                              </ListItemIcon>
                            </Tooltip>
                          ) : null}
                        </ListItem>
                      );
                    },
                  },
                  {
                    id: "invoiceIssuer",
                    format: (data) => {
                      if (!data) {
                        return "";
                      }
                      return (
                        getInvoiceIssuerData(invoiceIssuers, data)?.name || data
                      );
                    },
                  },
                  {
                    id: "depositAmount",
                    format: (data, d, row) => {
                      if (
                        !(
                          row.paymentStatus === "invoiced" ||
                          row.paymentStatus === "deposit"
                        )
                      ) {
                        return "";
                      }
                      return row.totalPrice - row.depositAmount;
                    },
                  },
                ]}
                // handleScrollClick: showDeviceNewTab,
                tableData={searchList}
                rowsPerPage={50}
              />
            </>
          ) : loading ? (
            <CircularProgress />
          ) : undefined}
        </Paper>
      </Grid>
    </Grid>
  );
}
